/* eslint-disable */
import apiClient from './apiClient';

// ==================== Types ====================

export interface Patient {
    id: number;
    first_name: string;
    last_name: string;
    identification?: string;
    medical_record_number?: string;
    birth_date?: string;
    gender?: boolean | null;
    phone?: string;
    mobile?: string;
    email?: string;
    address?: string;
    identification_type_id?: number;
    nationality_id?: number;
    municipal_district_id?: number;
    is_oncology?: boolean;
    status?: string;
    created_at?: string;
    updated_at?: string;
    // Relations
    identification_type?: { id: number; name: string };
    nationality?: { id: number; name: string };
    municipal_district?: { id: number; name: string };
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface CreatePatientRequest {
    first_name: string;
    last_name: string;
    identification: string;
    birth_date: string;
    gender: boolean;
    identification_type_id: number;
    nationality_id: number;
    phone?: string;
    mobile?: string;
    email?: string;
    address?: string;
}


// ==================== Allergy Types ====================

export interface Allergy {
    id: number;
    name: string;
    description?: string;
    category_id?: number;
    category?: AllergyCategory;
    created_at?: string;
    updated_at?: string;
}

export interface AllergyCategory {
    id: number;
    name: string;
    description?: string;
}

export interface PatientAllergy {
    id: number;
    patient_id: number;
    allergy_id: number;
    notes?: string;
    recorded_by?: number;
    recorded_at?: string;
    // Loaded relations
    name?: string;
    description?: string;
    category?: AllergyCategory;
}

// ==================== Clinical Summary Types ====================

/**
 * Mirrors GET /patients/{id}/summary — the chart header payload.
 * Deliberately smaller than the /history response, which carries ten
 * consultations plus appointments, admissions and insurance plans.
 */
export interface ClinicalSummary {
    allergies: SummaryAllergy[];
    latest_vitals: SummaryVitals | null;
    recent_medications: SummaryMedication[];
    last_consultation: SummaryConsultation | null;
    next_appointment: SummaryAppointment | null;
}

export interface SummaryAllergy {
    id: number;
    name: string;
    category: string | null;
    notes: string | null;
}

export interface SummaryVitals {
    consultation_id: number;
    /** The reading's own date — vitals may come from an earlier visit. */
    recorded_at: string;
    heart_rate: number | null;
    respiratory_rate: number | null;
    /** Pre-composed as "120/80", or null when either half is missing. */
    blood_pressure: string | null;
    temperature: string | null;
    weight: string | null;
    height: string | null;
    bmi: string | null;
    oxygen_saturation: number | null;
}

export interface SummaryMedication {
    medication_name: string;
    dosage: string | null;
    frequency: string | null;
    /** Free text ("7 días"), so vigency cannot be derived from it. */
    duration: string | null;
    instructions: string | null;
    prescribed_at: string;
    consultation_id: number;
}

export interface SummaryConsultation {
    id: number;
    date: string;
    doctor: string | null;
    chief_complaint: string | null;
}

export interface SummaryAppointment {
    id: number;
    date: string;
    time: string | null;
    doctor: string | null;
    reason: string | null;
    status: string | null;
}

// ==================== Contact Types ====================

export interface PatientContact {
    id: number;
    patient_id: number;
    name: string;
    phone?: string;
    mobile?: string;
    email?: string;
    relationship?: string;
    address?: string;
    is_primary: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface CreateContactRequest {
    name: string;
    phone?: string;
    mobile?: string;
    email?: string;
    relationship?: string;
    address?: string;
    is_primary?: boolean;
}

// ==================== Patient Service ====================

export const patientService = {
    async getAll(perPage = 15): Promise<PaginatedResponse<Patient>> {
        const response = await apiClient.get<PaginatedResponse<Patient>>(`/patients?per_page=${perPage}`);
        return response.data;
    },

    async getById(id: number): Promise<Patient> {
        const response = await apiClient.get<ApiResponse<Patient>>(`/patients/${id}`);
        return response.data.data;
    },

    async getWithHistory(id: number): Promise<Patient> {
        const response = await apiClient.get<any>(`/patients/${id}/history`);
        return response.data.data;
    },

    async getClinicalSummary(id: number): Promise<ClinicalSummary> {
        const response = await apiClient.get<ApiResponse<ClinicalSummary>>(`/patients/${id}/summary`);
        return response.data.data;
    },

    async search(term: string): Promise<Patient[]> {
        const response = await apiClient.get<ApiResponse<Patient[]>>(`/patients/search?term=${encodeURIComponent(term)}`);
        return response.data.data || [];
    },

    async create(data: CreatePatientRequest): Promise<Patient> {
        const response = await apiClient.post<ApiResponse<Patient>>('/patients', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<CreatePatientRequest>): Promise<void> {
        await apiClient.put(`/patients/${id}`, data);
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/patients/${id}`);
    },

    // Allergies
    async getAllergies(patientId: number): Promise<PatientAllergy[]> {
        const response = await apiClient.get<ApiResponse<PatientAllergy[]>>(`/patients/${patientId}/allergies`);
        return response.data.data;
    },

    async addAllergy(patientId: number, allergyId: number, notes?: string): Promise<PatientAllergy[]> {
        const response = await apiClient.post<ApiResponse<PatientAllergy[]>>(`/patients/${patientId}/allergies`, {
            allergy_id: allergyId,
            notes,
        });
        return response.data.data;
    },

    async removeAllergy(patientId: number, allergyId: number): Promise<void> {
        await apiClient.delete(`/patients/${patientId}/allergies/${allergyId}`);
    },

    // Contacts
    async getContacts(patientId: number): Promise<PatientContact[]> {
        const response = await apiClient.get<ApiResponse<PatientContact[]>>(`/patients/${patientId}/contacts`);
        return response.data.data;
    },

    async addContact(patientId: number, data: CreateContactRequest): Promise<PatientContact> {
        const response = await apiClient.post<ApiResponse<PatientContact>>(`/patients/${patientId}/contacts`, data);
        return response.data.data;
    },

    async updateContact(patientId: number, contactId: number, data: Partial<CreateContactRequest>): Promise<PatientContact> {
        const response = await apiClient.put<ApiResponse<PatientContact>>(`/patients/${patientId}/contacts/${contactId}`, data);
        return response.data.data;
    },

    async deleteContact(patientId: number, contactId: number): Promise<void> {
        await apiClient.delete(`/patients/${patientId}/contacts/${contactId}`);
    },
};

// ==================== Allergy Service (Catalog) ====================

export const allergyService = {
    async getAll(params: { per_page?: number; search?: string; category_id?: number } = {}): Promise<PaginatedResponse<Allergy>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.category_id) queryParams.append('category_id', params.category_id.toString());

        const response = await apiClient.get<PaginatedResponse<Allergy>>(`/allergies?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<Allergy> {
        const response = await apiClient.get<ApiResponse<Allergy>>(`/allergies/${id}`);
        return response.data.data;
    },

    async create(data: { name: string; description?: string; category_id?: number }): Promise<Allergy> {
        const response = await apiClient.post<ApiResponse<Allergy>>('/allergies', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{ name: string; description: string; category_id: number }>): Promise<Allergy> {
        const response = await apiClient.put<ApiResponse<Allergy>>(`/allergies/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/allergies/${id}`);
    },

    // Categories
    async getCategories(): Promise<AllergyCategory[]> {
        const response = await apiClient.get<ApiResponse<AllergyCategory[]>>('/allergies/categories');
        return response.data.data;
    },

    async createCategory(data: { name: string; description?: string }): Promise<AllergyCategory> {
        const response = await apiClient.post<ApiResponse<AllergyCategory>>('/allergies/categories', data);
        return response.data.data;
    },
};

export interface PatientDocument {
    id: number;
    original_name: string;
    mime_type: string;
    file_size: number;
    source: string | null;
    consultation_id: number | null;
    url: string;
    created_at: string;
}

export const patientDocumentService = {
    async list(patientId: number): Promise<PatientDocument[]> {
        const response = await apiClient.get<ApiResponse<PatientDocument[]>>(`/patients/${patientId}/documents`);
        return response.data.data;
    },

    async upload(patientId: number, file: File, consultationId?: number, source?: string): Promise<PatientDocument> {
        const formData = new FormData();
        formData.append('file', file);
        if (consultationId) formData.append('consultation_id', String(consultationId));
        if (source) formData.append('source', source);

        const response = await apiClient.post<ApiResponse<PatientDocument>>(
            `/patients/${patientId}/documents`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data.data;
    },

    getUrl(patientId: number, documentId: number): string {
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
        return `${baseUrl}/api/v1/patients/${patientId}/documents/${documentId}`;
    },

    async delete(patientId: number, documentId: number): Promise<void> {
        await apiClient.delete(`/patients/${patientId}/documents/${documentId}`);
    },
};

export default patientService;
